import { bindActionCreators } from '@reduxjs/toolkit';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { closeFilter, closeFoldout } from '../../actions/filters';
import type QueryFilter from '../../helpers/query-filter';
import type { RootState } from '../../reducers';
import { requestStatus } from '../../shared/statuses';
import { getActiveFacet, getQueryFilter, getSearchHitCount } from '../../utils/reselectors';
import FilterWxPanel from './filter-wx-panel';
import SelectFilter from './select-filter';
import SelectFilterLocation from './select-filter-location';
import SelectFilterRange from './select-filter-range';

interface SelectedFilterModalProps {
  onRequestSearch: (queryFilter: QueryFilter) => void;
}

const SelectedFilterModal = ({ onRequestSearch }: SelectedFilterModalProps) => {
  const { t } = useTranslation('search');
  const dispatch = useDispatch();

  const searching = useSelector<RootState, boolean>(
    (s) => s.search.requestStatus === requestStatus.FETCHING_LIST,
  );
  const facet = useSelector(getActiveFacet);
  const isExcludedFacet = useSelector<RootState, boolean>((s) => s.filters.isExcludedFacet);
  const queryFilter = useSelector(getQueryFilter);
  const numberOfHits = useSelector(getSearchHitCount);

  const actions = bindActionCreators({ closeFoldout, closeFilter }, dispatch);

  const onReset = () => {
    if (facet) {
      const nextQueryFilter = queryFilter.remove(facet.id).removeRange(facet.id);
      onRequestSearch(nextQueryFilter);
    }
  };

  const onCloseFoldout = () => actions.closeFoldout();
  const onCloseFilter = () => actions.closeFilter();

  if (!facet) {
    return null;
  }

  let filterSelector = null;
  let filterBodyBlock = null;
  let filterModifier = '';
  const isResetable = queryFilter.has(facet.id) || queryFilter.hasRange(facet.id);
  if (facet.isLocationFacet) {
    return (
      <SelectFilterLocation
        facet={facet}
        queryFilter={queryFilter}
        onRequestSearch={onRequestSearch}
        onCloseFoldout={onCloseFoldout}
      />
    );
  } else if (facet.isRangeFacet) {
    filterSelector = (
      <SelectFilterRange
        key={`select-filter-range-${facet.id}`}
        facet={facet}
        queryFilter={queryFilter}
        onRequestSearch={onRequestSearch}
        onCloseFoldout={onCloseFoldout}
        onReset={isResetable ? onReset : undefined}
      />
    );
  } else {
    filterSelector = (
      <SelectFilter
        facet={facet}
        isExcludedFacet={isExcludedFacet}
        queryFilter={queryFilter}
        onRequestSearch={onRequestSearch}
      />
    );
    filterModifier = 'list';
  }

  if (searching) filterBodyBlock = <div className="filter-modal-body-block" />;

  return (
    <FilterWxPanel
      modifiers={filterModifier}
      title={facet.displayName}
      onRequestSearch={onRequestSearch}
    >
      {filterBodyBlock}
      {filterSelector}
      <div className="filter-panel-body-footer">
        <div className="filter-panel-body-footer-action-container">
          <div className="filter-panel-body-footer-action hidden-sm">
            <button
              type="button"
              data-wx-button="light narrow full-width"
              onClick={onCloseFilter}
              title={t('Show all filters', { ns: 'search' })}
            >
              <div className="button-icon">
                <i className="icon-arrow-left no-margin" />
              </div>
              <div className="button-text">{t('All filters', { ns: 'search' })}</div>
            </button>
          </div>
          <div className="filter-panel-body-footer-action">
            <button
              type="button"
              data-wx-button="action narrow full-width"
              onClick={onCloseFoldout}
              title={t('Show number of results', { ns: 'search', count: numberOfHits })}
              data-testid="filter-modal-show-results-button"
            >
              <div className="button-icon">
                <i className="icon-search no-margin" />
              </div>
              <div className="button-text">
                {t('Show number of results', { ns: 'search', count: numberOfHits })}
              </div>
            </button>
          </div>
        </div>
      </div>
    </FilterWxPanel>
  );
};

export default SelectedFilterModal;
